import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BuildIcon from "@mui/icons-material/Build";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsIcon from "@mui/icons-material/Groups";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import SearchIcon from "@mui/icons-material/Search";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { playerHandleSuffix } from "@bw/shared/components";
import {
  useCourtStateStream,
  useReservationStateStream,
} from "@bw/shared/hooks";
import {
  formatCourtTagLabel,
  formatCourtLineSummary,
  formatCourtStatusAnnouncement,
  getQueueAssignmentPolicyCopy,
  getCourtStateLabel,
} from "@bw/shared/utils";

import {
  listCourts,
  listQueueRecoveryReasons,
  updateCourt,
  getCourtBoard,
  type Court as ApiCourt,
  type CourtStatus,
  type CourtBoardResponse,
  type QueueRecoveryReason,
} from "@/libs/facilityApi";
import {
  previewCourtAvailability,
  type CourtAvailabilityCourtVerdict,
  type CourtAvailabilityPreview,
} from "@/libs/courtAvailabilityApi";
import type {
  CourtBoardEntry,
  CourtBoardLineGroup,
  CourtBoardPlayingGroup,
  CourtBoardPresence,
} from "@bw/shared/types/generated";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { useMyFacilityActions } from "@/hooks/use-my-facility-actions";
import {
  assignCourt,
  clearFacilityQueue,
  closeFacilityQueue,
  endSession,
  getQueueStats,
  listQueue,
  reassignCourt,
  removeWaitingEntry,
  reopenFacilityQueue,
  skipWaitingEntry,
  type QueueOperatorAction,
  type QueueEntry as ApiQueueEntry,
  type QueueStats,
} from "@/libs/queueApi";
import {
  CustomerPicker,
  type SelectedCustomer,
} from "@/components/customer-picker";
import {
  addWalkInParty,
  searchActiveWalkInCustomers,
  type StaffWalkInPartyRequest,
} from "./walk-in-api";

/**
 * Drop-in Queue Workspace — front-desk console for walk-in players and live
 * court occupancy. Lets a front-desk operator see who is waiting, how long
 * they've been waiting, which courts are open, and call players to a court.
 *
 * Fully backend-wired against @/libs/queueApi + @/libs/facilityApi. There is no
 * seed/demo fallback: an empty queue renders an honest empty state and a failed
 * load renders an honest error banner.
 *
 * Court occupancy and assignability come from the backend court board and queue
 * stats. The basic court list exposes only enabled/maintenance, so it is never
 * used as proof that a court is available. Calling a party to a court is a real
 * action (POST /queue/{entry}/assign) followed by a refresh.
 */

type Court = {
  id: string;
  label: string;
  tags: string[];
  maxPlayers: number | null;
};

type QueueEntry = {
  id: string;
  targetCourtId: string | null;
  position: number | null;
  joinedAt: Date;
  preference?: "singles" | "doubles" | "any";
  // Identity fields from the enriched backend response (slice 2).
  // null/absent when backend could not resolve the lead player.
  leadUserId?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  groupSize?: number | null;
  waitingReason?: ApiQueueEntry["waiting_reason"];
  waitingUntil?: string | null;
  predictedAssignmentType?: ApiQueueEntry["predicted_assignment_type"];
};

type FacilityOperation = {
  facilityId: string;
  generation: number;
};

type ManualAssignmentConfirmation = {
  entry: QueueEntry;
  entryLabel: string;
  court: Court;
  startsAt: string;
  endsAt: string;
  assignmentDurationMinutes: number;
  timezone: string;
};

type WaitingRecoveryAction = {
  kind: "skip" | "remove";
  entryId: string;
  label: string;
};

type RecoveryAction = QueueRecoveryReason["actions"][number];

const BOARD_UNAVAILABLE_MESSAGE =
  "Court board unavailable. Court availability and occupancy are unknown.";

function isCourtReadyToAssign(entry: CourtBoardEntry): boolean {
  return (
    entry.operational_state === "operational" &&
    entry.occupancy_state === "available" &&
    entry.availability_state === "assignable"
  );
}

function getCourtAssignmentLabel(entry: CourtBoardEntry): string {
  if (
    entry.operational_state === "operational" &&
    entry.occupancy_state === "available" &&
    !isCourtReadyToAssign(entry)
  ) {
    return "Not assignable";
  }
  return getCourtStateLabel({
    operationalState: entry.operational_state,
    occupancyState: entry.occupancy_state,
  });
}

function minutesAgo(d: Date): number {
  const now = new Date();
  return Math.max(0, Math.round((now.getTime() - d.getTime()) / 60_000));
}

function fmtMin(m: number): string {
  if (m < 1) return "just now";
  if (m === 1) return "1 min";
  return `${m} min`;
}

function formatFacilityTime(
  timestamp: string,
  timezone: string,
  includeZone = false,
): string {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
    ...(includeZone ? { timeZoneName: "short" as const } : {}),
  });
}

function actionErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

// Position-only label — used as the honest fallback when display_name is absent
// or unresolved (no-UID-in-UI rule).
function partyLabel(entry: QueueEntry): string {
  return entry.position ? `Party ${entry.position}` : "Party (no position)";
}

const UUID_SHAPED_NAME =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function looksLikeRawIdentity(value: string): boolean {
  return (
    UUID_SHAPED_NAME.test(value) ||
    (value.length >= 20 && /^[A-Za-z0-9_-]+$/.test(value))
  );
}

// Returns true when display_name is a resolved, non-id name that is safe to
// render. Guards: null/empty display_name, a null lead_user_id (no uid ⇒ not
// resolved — also makes the #abcd handle derivation sound), and the
// id-equals-name backend fallback. A `true` result implies leadUserId is
// non-null, so the `!` assertions on the handle + avatar narrow safely.
function isResolvedName(
  displayName: string | null | undefined,
  leadUserId: string | null | undefined,
): leadUserId is string {
  const normalizedName = displayName?.trim();
  if (!normalizedName) return false;
  if (!leadUserId) return false;
  if (normalizedName === leadUserId.trim()) return false;
  if (looksLikeRawIdentity(normalizedName)) return false;
  return true;
}

function mapCourt(court: ApiCourt): Court {
  return {
    id: court.court_id,
    label: court.name,
    tags: court.tags ?? [],
    maxPlayers: court.max_players ?? null,
  };
}

function mapQueueEntry(entry: ApiQueueEntry): QueueEntry {
  return {
    id: entry.entry_id,
    targetCourtId: entry.target_court_id ?? null,
    position: entry.position ?? null,
    joinedAt: new Date(entry.joined_at),
    preference:
      entry.mode === "ranked_singles"
        ? "singles"
        : entry.mode === "ranked_doubles"
          ? "doubles"
          : "any",
    leadUserId: entry.lead_user_id,
    displayName: entry.display_name,
    avatarUrl: entry.avatar_url,
    groupSize: entry.group_size,
    waitingReason: entry.waiting_reason,
    waitingUntil: entry.waiting_until,
    predictedAssignmentType: entry.predicted_assignment_type,
  };
}

function waitingPolicyCopy(entry: QueueEntry): string | null {
  if (entry.waitingReason === "awaiting_compatible_players") {
    return "Waiting for compatible players";
  }
  if (
    entry.waitingReason === "awaiting_target_occupancy" &&
    entry.predictedAssignmentType === "fresh"
  ) {
    return "Fresh court: building target occupancy";
  }
  if (entry.waitingReason === "awaiting_court_availability") {
    return "Waiting for court availability";
  }
  if (
    entry.waitingReason === "assignment_pending" &&
    entry.predictedAssignmentType === "fresh"
  ) {
    return "Fresh court assignment pending";
  }
  if (
    entry.waitingReason === "assignment_pending" &&
    entry.predictedAssignmentType === "top_off"
  ) {
    return "Active-court fill pending";
  }
  return null;
}

type ManualCourtOption = {
  court: Court;
  available: boolean;
  status: string;
  capacity: number | null;
  availableUntil: string | null;
};

function ManualCourtPicker({
  entryLabel,
  controlId,
  accessibleName,
  options,
  value,
  disabled,
  onValueChange,
}: {
  entryLabel: string;
  controlId?: string;
  accessibleName?: string;
  options: ManualCourtOption[];
  value: string;
  disabled: boolean;
  onValueChange: (courtId: string) => void;
}) {
  const selectedOption = options.find(
    (option) => option.court.id === value && option.available,
  );
  const placeholder =
    options.length === 0
      ? "No courts"
      : options.some((option) => option.available)
        ? "Choose court"
        : "Review courts";

  return (
    <Autocomplete
      id={controlId}
      options={options}
      value={selectedOption ?? null}
      disabled={disabled}
      openOnFocus
      clearOnEscape
      getOptionLabel={(option) => option.court.label}
      getOptionDisabled={(option) => !option.available}
      isOptionEqualToValue={(option, selected) =>
        option.court.id === selected.court.id
      }
      filterOptions={(availableOptions, state) => {
        const query = state.inputValue.trim().toLowerCase();
        if (!query) return availableOptions;
        return availableOptions.filter((option) =>
          [
            option.court.label,
            ...option.court.tags.map(formatCourtTagLabel),
            option.status,
          ].some((keyword) => keyword.toLowerCase().includes(query)),
        );
      }}
      onChange={(_event, option) =>
        onValueChange(option?.available ? option.court.id : "")
      }
      noOptionsText="No courts found."
      sx={{ width: { xs: 160, sm: 192 }, maxWidth: "100%" }}
      slotProps={{
        listbox: {
          "aria-label": `Courts for ${entryLabel}`,
          sx: { maxHeight: 256 },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              "aria-label": accessibleName ?? `Court for ${entryLabel}`,
              title: selectedOption?.court.label ?? placeholder,
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.court.id}
          title={option.status}
          sx={{
            display: "flex",
            alignItems: "flex-start !important",
            gap: 1,
            whiteSpace: "normal",
            opacity: option.available ? 1 : 0.6,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
            >
              {option.court.label}
            </Typography>
            <Stack
              direction="row"
              useFlexGap
              sx={{ flexWrap: "wrap", columnGap: 1, rowGap: 0.25 }}
            >
              {option.court.tags.map((tag) => (
                <Typography
                  key={tag}
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    textTransform:
                      tag === "open_play" || tag === "drop_in"
                        ? "none"
                        : "capitalize",
                  }}
                >
                  {tag === "open_play" || tag === "drop_in"
                    ? formatCourtTagLabel(tag)
                    : tag.replace(/[_-]+/g, " ")}
                </Typography>
              ))}
              {option.capacity != null ? (
                <Typography variant="caption" color="text.secondary">
                  Capacity {option.capacity}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                {option.status}
              </Typography>
            </Stack>
          </Box>
          {option.court.id === value ? (
            <CheckIcon fontSize="small" aria-hidden="true" />
          ) : null}
        </Box>
      )}
    />
  );
}

function RecoveryFields({
  action,
  reasons,
  reasonCode,
  note,
  disabled,
  onReasonChange,
  onNoteChange,
}: {
  action: RecoveryAction;
  reasons: QueueRecoveryReason[];
  reasonCode: string;
  note: string;
  disabled: boolean;
  onReasonChange: (value: string) => void;
  onNoteChange: (value: string) => void;
}) {
  return (
    <Stack spacing={2} sx={{ py: 0.5 }}>
      <FormControl fullWidth required>
        <InputLabel id={`recovery-reason-${action}-label`} shrink>
          Reason
        </InputLabel>
        <Select
          labelId={`recovery-reason-${action}-label`}
          id={`recovery-reason-${action}`}
          aria-label="Reason"
          aria-required="true"
          label="Reason"
          value={reasonCode}
          onChange={(event) => onReasonChange(event.target.value)}
          disabled={disabled}
          displayEmpty
          renderValue={(value) =>
            value
              ? (reasons.find((reason) => reason.code === value)?.label ??
                value)
              : "Choose a reason"
          }
        >
          {reasons.map((reason) => (
            <MenuItem key={reason.reason_id} value={reason.code}>
              {reason.label}
            </MenuItem>
          ))}
        </Select>
        {reasons.length === 0 ? (
          <FormHelperText>No reasons are available.</FormHelperText>
        ) : null}
      </FormControl>
      <TextField
        id={`recovery-note-${action}`}
        label="Private note"
        value={note}
        multiline
        rows={3}
        placeholder="Optional staff-only context"
        disabled={disabled}
        onChange={(event) => onNoteChange(event.target.value)}
        helperText="This note is recorded for staff and is not shown to players."
        slotProps={{ htmlInput: { maxLength: 500 } }}
      />
    </Stack>
  );
}

type OnCourtEntry = {
  courtId: string;
  courtName: string;
  playingGroups: OnCourtPlayingGroup[];
};

type OnCourtPlayingGroup = {
  playingGroupKey: string;
  groupReference: string;
  members: CourtBoardPlayingGroup["members"];
  assignmentId: string | null;
  label: string;
  groupSize: number;
  expiresAt: string | null;
  assignmentReason: string | null;
  remainingMinutes: number | null;
};

type ReassignableOnCourtPlayingGroup = OnCourtPlayingGroup & {
  assignmentId: string;
  expiresAt: string;
};

function isReassignablePlayingGroup(
  group: OnCourtPlayingGroup,
): group is ReassignableOnCourtPlayingGroup {
  return group.assignmentId !== null && group.expiresAt !== null;
}

function playingGroupOpaquePart(playingGroupKey: string): string {
  return playingGroupKey.startsWith("playing_")
    ? playingGroupKey.slice("playing_".length)
    : playingGroupKey;
}

function playingGroupReferences(
  groups: CourtBoardPlayingGroup[],
): Map<string, string> {
  const opaqueParts = groups.map((group) => ({
    key: group.playing_group_key,
    opaque: playingGroupOpaquePart(group.playing_group_key),
  }));
  let width = Math.min(
    8,
    Math.max(0, ...opaqueParts.map((group) => group.opaque.length)),
  );
  while (width < Math.max(...opaqueParts.map((group) => group.opaque.length))) {
    const references = opaqueParts.map((group) => group.opaque.slice(-width));
    if (new Set(references).size === references.length) break;
    width += 4;
  }
  return new Map(
    opaqueParts.map((group) => [
      group.key,
      group.opaque.slice(-width).toUpperCase(),
    ]),
  );
}

function playingGroupReference(playingGroupKey: string): string {
  const opaquePart = playingGroupKey.startsWith("playing_")
    ? playingGroupKey.slice("playing_".length)
    : playingGroupKey;
  return opaquePart.slice(-8).toUpperCase();
}

function playingGroupLabel(
  group: CourtBoardPlayingGroup,
  groupReference: string,
): string {
  const kind =
    group.player_count === 1 ? "Solo" : `Party of ${group.player_count}`;
  const names = group.members.flatMap((member) =>
    isResolvedName(member.display_name, member.presence_id)
      ? [member.display_name!]
      : [],
  );
  const roster = names.length > 0 ? names.join(", ") : "Roster unavailable";
  return `${kind}, ${roster}, reference ${groupReference}`;
}

function projectRemainingMinutes(
  expiresAt: string,
  snapshotTimeMs: number,
): number | null {
  const expiresAtMs = Date.parse(expiresAt);
  if (!Number.isFinite(expiresAtMs)) return null;
  return Math.max(0, Math.ceil((expiresAtMs - snapshotTimeMs) / 60_000));
}

function mapOnCourtEntry(
  entry: CourtBoardEntry,
  snapshotTimeMs: number,
): OnCourtEntry {
  const playingGroups = entry.playing_groups ?? [];
  const groupReferences = playingGroupReferences(playingGroups);
  return {
    courtId: entry.court_id,
    courtName: entry.name,
    playingGroups: playingGroups.map((group) => {
      const operatorAssignment = group.operator_assignment ?? null;
      const groupReference =
        groupReferences.get(group.playing_group_key) ??
        playingGroupReference(group.playing_group_key);
      return {
        playingGroupKey: group.playing_group_key,
        groupReference,
        members: group.members,
        assignmentId: operatorAssignment?.assignment_id ?? null,
        label: playingGroupLabel(group, groupReference),
        groupSize: group.player_count,
        expiresAt: operatorAssignment?.expires_at ?? null,
        assignmentReason: operatorAssignment
          ? getQueueAssignmentPolicyCopy({
              assignmentType: operatorAssignment.assignment_type,
              policyReason: operatorAssignment.policy_reason,
            })
          : null,
        remainingMinutes: operatorAssignment
          ? projectRemainingMinutes(
              operatorAssignment.expires_at,
              snapshotTimeMs,
            )
          : null,
      };
    }),
  };
}

type ReassignAction = {
  group: ReassignableOnCourtPlayingGroup;
  sourceCourtId: string;
  sourceCourtName: string;
};

export interface QueueWorkspaceProps {
  // When true, the page-level <h1> title + facility subtitle are suppressed so a
  // host shell (the Drop-in service home Dashboard tab) can own the single page
  // title (ui-quality one-title rule). The status strip, waiting queue, court
  // board/details, and Close / Reopen / Clear actions all still render. By
  // default, the standalone /queue route owns and renders the title.
  embedded?: boolean;
  // A host increments this after an external operation changes queue or court
  // state. The workspace consumes the signal through its authoritative refresh,
  // keeping every rendered queue, stats, and court-board projection in sync.
  refreshGeneration?: number;
}

export function QueueWorkspace({
  embedded = false,
  refreshGeneration: requestedRefreshGeneration = 0,
}: QueueWorkspaceProps = {}) {
  const theme = useTheme();
  const compactWorkspace = useMediaQuery(theme.breakpoints.down("lg"));
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const { actions: facilityActions, isLoading: facilityActionsLoading } =
    useMyFacilityActions(facilityId);
  const canManageCourts =
    !facilityActionsLoading && facilityActions.includes("facility:court");
  const [courts, setCourts] = useState<Court[]>([]);
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [onCourt, setOnCourt] = useState<OnCourtEntry[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [board, setBoard] = useState<CourtBoardResponse | null>(null);
  const [boardFacilityId, setBoardFacilityId] = useState<string | null>(null);
  const [boardError, setBoardError] = useState<string | null>(null);
  const [snapshotCurrent, setSnapshotCurrent] = useState(false);
  const [refreshInFlight, setRefreshInFlight] = useState(false);
  const [loadedFacilityId, setLoadedFacilityId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [workspaceView, setWorkspaceView] = useState<"queue" | "courts">(
    "queue",
  );
  const renderCourtTiles = !compactWorkspace || workspaceView === "courts";
  const [manualCourtSelections, setManualCourtSelections] = useState<
    Record<string, string>
  >({});
  const [courtAvailability, setCourtAvailability] =
    useState<CourtAvailabilityPreview | null>(null);
  const [courtAvailabilityStatus, setCourtAvailabilityStatus] = useState<
    "idle" | "ready" | "error"
  >("idle");
  const [assignmentConfirmation, setAssignmentConfirmation] =
    useState<ManualAssignmentConfirmation | null>(null);
  const assignmentConfirmationRef = useRef<ManualAssignmentConfirmation | null>(
    null,
  );
  assignmentConfirmationRef.current = assignmentConfirmation;

  // Courts-panel "End session" confirm dialog (irreversible: frees the court).
  const [endSessionCourt, setEndSessionCourt] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [endSessionBusy, setEndSessionBusy] = useState(false);
  const [waitingRecovery, setWaitingRecovery] =
    useState<WaitingRecoveryAction | null>(null);
  const [recoveryReasons, setRecoveryReasons] = useState<QueueRecoveryReason[]>(
    [],
  );
  const [recoveryCatalogError, setRecoveryCatalogError] = useState<
    string | null
  >(null);
  const [recoveryReasonCode, setRecoveryReasonCode] = useState("");
  const [recoveryNote, setRecoveryNote] = useState("");
  const [recoveryDialogError, setRecoveryDialogError] = useState<string | null>(
    null,
  );
  const [reassignAction, setReassignAction] = useState<ReassignAction | null>(
    null,
  );
  const [reassignPreview, setReassignPreview] =
    useState<CourtAvailabilityPreview | null>(null);
  const [reassignCourtId, setReassignCourtId] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);

  // Add walk-in dialog
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [walkInMode, setWalkInMode] =
    useState<StaffWalkInPartyRequest["mode"]>("normal");
  const [walkInCustomer, setWalkInCustomer] = useState<SelectedCustomer | null>(
    null,
  );
  const [walkInSaving, setWalkInSaving] = useState(false);
  const [walkInError, setWalkInError] = useState<string | null>(null);

  // Queue-level confirm dialogs (close / reopen / clear)
  const [confirmAction, setConfirmAction] = useState<
    "close" | "reopen" | "clear" | null
  >(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const handledHostRefreshGeneration = useRef(requestedRefreshGeneration);
  const refreshGeneration = useRef(0);
  const refreshRequestId = useRef(0);
  const reassignPreviewRequestId = useRef(0);
  const revisionFreeReconcile = useRef<Promise<boolean> | null>(null);
  const lastBoardRevision = useRef(0);
  const pendingBoardRevision = useRef<number | null>(null);
  const currentFacilityId = useRef<string | null>(facilityId ?? null);
  const queueSessionDurationMinutes = useRef(30);
  const queueMinimumRemainingMinutes = useRef(20);
  currentFacilityId.current = facilityId ?? null;
  queueSessionDurationMinutes.current =
    facility?.queue_session_duration_minutes ?? 30;
  queueMinimumRemainingMinutes.current =
    facility?.queue_minimum_remaining_minutes ?? 20;

  function captureFacilityOperation(): FacilityOperation | null {
    if (!facilityId || facilityId !== currentFacilityId.current) return null;
    return { facilityId, generation: refreshGeneration.current };
  }

  function isCurrentFacilityOperation(operation: FacilityOperation): boolean {
    return (
      operation.generation === refreshGeneration.current &&
      operation.facilityId === currentFacilityId.current
    );
  }

  const refresh = useCallback(
    async (
      requestedFacilityId: string,
      facilityGeneration = refreshGeneration.current,
      minimumBoardRevision?: number,
    ) => {
      const isCurrentRequest = () =>
        facilityGeneration === refreshGeneration.current &&
        requestedFacilityId === currentFacilityId.current;
      if (!isCurrentRequest()) return false;

      const requestId = ++refreshRequestId.current;
      setRefreshInFlight(true);
      try {
        const [courtRows, queueRows, queueStats, courtBoardResult] =
          await Promise.all([
            listCourts(requestedFacilityId),
            listQueue(requestedFacilityId, "waiting"),
            getQueueStats(requestedFacilityId),
            getCourtBoard(requestedFacilityId)
              .then((courtBoard) => ({ ok: true as const, courtBoard }))
              .catch(() => ({ ok: false as const })),
          ]);

        let availabilityResult:
          | { ok: true; preview: CourtAvailabilityPreview }
          | { ok: false }
          | null = null;
        if (
          queueStats.queue_assignment_mode === "manual" &&
          courtRows.length > 0
        ) {
          const requestedDurationMinutes = queueSessionDurationMinutes.current;
          const startsAt = new Date();
          const endsAt = new Date(
            startsAt.getTime() + requestedDurationMinutes * 60_000,
          );
          availabilityResult = await previewCourtAvailability(
            requestedFacilityId,
            {
              court_ids: courtRows.map((court) => court.court_id),
              start_time: startsAt.toISOString(),
              end_time: endsAt.toISOString(),
              minimum_duration_minutes: queueMinimumRemainingMinutes.current,
            },
          )
            .then((preview) => ({ ok: true as const, preview }))
            .catch(() => ({ ok: false as const }));
        }

        if (!isCurrentRequest() || requestId !== refreshRequestId.current)
          return false;
        setCourtAvailability(
          availabilityResult?.ok ? availabilityResult.preview : null,
        );
        setCourtAvailabilityStatus(
          availabilityResult === null
            ? "idle"
            : availabilityResult.ok
              ? "ready"
              : "error",
        );
        if (courtBoardResult.ok) {
          const courtBoard = courtBoardResult.courtBoard;
          const requiredBoardRevision = Math.max(
            minimumBoardRevision ?? 0,
            pendingBoardRevision.current ?? 0,
          );
          if (
            courtBoard.revision < lastBoardRevision.current ||
            courtBoard.revision < requiredBoardRevision
          ) {
            setRefreshInFlight(false);
            return false;
          }
          const courtConfigById = new Map(
            courtRows.map((court) => [court.court_id, court]),
          );
          setCourts(
            (courtBoard.courts ?? []).map((entry) => {
              const courtConfig = courtConfigById.get(entry.court_id);
              return {
                id: entry.court_id,
                label: entry.name,
                tags: courtConfig?.tags ?? [],
                maxPlayers: entry.capacity,
              };
            }),
          );
          setQueue(queueRows.map(mapQueueEntry));
          setStats(queueStats);
          lastBoardRevision.current = courtBoard.revision;
          setBoard(courtBoard);
          setBoardFacilityId(requestedFacilityId);
          const snapshotTimeMs = Date.now();
          setOnCourt(
            (courtBoard.courts ?? [])
              .filter((court) => court.occupancy_state === "in_play")
              .map((court) => mapOnCourtEntry(court, snapshotTimeMs)),
          );
          setBoardError(null);
          if (
            pendingBoardRevision.current !== null &&
            courtBoard.revision >= pendingBoardRevision.current
          ) {
            pendingBoardRevision.current = null;
          }
        } else {
          setCourts(courtRows.map(mapCourt));
          setQueue(queueRows.map(mapQueueEntry));
          setStats(queueStats);
          setBoardError(BOARD_UNAVAILABLE_MESSAGE);
          setEndSessionCourt(null);
        }
        setLoadedFacilityId(requestedFacilityId);
        if (!courtBoardResult.ok) {
          throw new Error(BOARD_UNAVAILABLE_MESSAGE);
        }
        setSnapshotCurrent(true);
        setRefreshInFlight(false);
        ++reassignPreviewRequestId.current;
        setReassignAction(null);
        setReassignPreview(null);
        setReassignCourtId("");
        setReassignBusy(false);
        setError(null);
        return true;
      } catch (error) {
        if (!isCurrentRequest() || requestId !== refreshRequestId.current)
          return false;

        // Retain the last verified data for recovery, but invalidate every live
        // projection and mutation until a complete refresh succeeds.
        setManualCourtSelections({});
        setCourtAvailability(null);
        setCourtAvailabilityStatus("error");
        setAssignmentConfirmation(null);
        setEndSessionCourt(null);
        setSnapshotCurrent(false);
        setRefreshInFlight(false);
        setLoadedFacilityId(requestedFacilityId);
        throw error;
      }
    },
    [],
  );

  const settleFacilityAction = useCallback(
    async (operation: FacilityOperation, mutate: () => Promise<unknown>) => {
      let mutationSucceeded = false;
      let mutationError: unknown = null;
      try {
        await mutate();
        mutationSucceeded = true;
      } catch (error) {
        mutationError = error;
      }

      let reconciliationCompleted = false;
      let reconciliationError: unknown = null;
      const operationIsCurrent = () =>
        operation.generation === refreshGeneration.current &&
        operation.facilityId === currentFacilityId.current;
      if (operationIsCurrent()) {
        try {
          await refresh(operation.facilityId, operation.generation);
          reconciliationCompleted = true;
        } catch (error) {
          reconciliationError = error;
        }
      }

      const boardAlreadyReportsError =
        reconciliationError instanceof Error &&
        reconciliationError.message === BOARD_UNAVAILABLE_MESSAGE;
      return {
        current: operationIsCurrent(),
        mutationSucceeded,
        reconciliationCompleted,
        error:
          mutationError ??
          (boardAlreadyReportsError ? null : reconciliationError),
      };
    },
    [refresh],
  );

  useEffect(() => {
    if (!facilityId) return;
    let active = true;
    const facilityGeneration = ++refreshGeneration.current;
    setLoadedFacilityId(null);
    setCourts([]);
    setQueue([]);
    setOnCourt([]);
    setStats(null);
    setBoard(null);
    setBoardFacilityId(null);
    revisionFreeReconcile.current = null;
    lastBoardRevision.current = 0;
    pendingBoardRevision.current = null;
    setBoardError(null);
    setSnapshotCurrent(false);
    setRefreshInFlight(false);
    setError(null);
    setBusyAction(null);
    setWorkspaceView("queue");
    setManualCourtSelections({});
    setCourtAvailability(null);
    setCourtAvailabilityStatus("idle");
    setAssignmentConfirmation(null);
    setWalkInOpen(false);
    setWalkInCustomer(null);
    setWalkInSaving(false);
    setWalkInError(null);
    setEndSessionCourt(null);
    setEndSessionBusy(false);
    setWaitingRecovery(null);
    setRecoveryReasonCode("");
    setRecoveryNote("");
    setRecoveryDialogError(null);
    setReassignAction(null);
    setReassignPreview(null);
    setReassignCourtId("");
    setReassignBusy(false);
    setConfirmAction(null);
    setConfirmBusy(false);
    setLoading(true);
    const isCurrentEffect = () =>
      active &&
      facilityGeneration === refreshGeneration.current &&
      facilityId === currentFacilityId.current;
    (async () => {
      try {
        await refresh(facilityId, facilityGeneration);
      } catch (err) {
        if (isCurrentEffect()) {
          setError(
            err instanceof Error && err.message === BOARD_UNAVAILABLE_MESSAGE
              ? null
              : err instanceof Error
                ? err.message
                : "Failed to load the queue.",
          );
          setLoadedFacilityId(facilityId);
        }
      } finally {
        if (isCurrentEffect()) setLoading(false);
      }
    })();
    return () => {
      active = false;
      ++refreshGeneration.current;
    };
  }, [facilityId, refresh]);

  useEffect(() => {
    if (!facilityId) {
      setRecoveryReasons([]);
      setRecoveryCatalogError(null);
      return;
    }
    let active = true;
    setRecoveryReasons([]);
    setRecoveryCatalogError(null);
    listQueueRecoveryReasons(facilityId)
      .then((reasons) => {
        if (active && facilityId === currentFacilityId.current) {
          setRecoveryReasons(reasons);
        }
      })
      .catch((error) => {
        if (active && facilityId === currentFacilityId.current) {
          setRecoveryCatalogError(
            actionErrorMessage(error, "Recovery reasons are unavailable."),
          );
        }
      });
    return () => {
      active = false;
    };
  }, [facilityId]);

  const reconcileCourtState = useCallback(
    (minimumBoardRevision?: number) => {
      if (!facilityId) return Promise.resolve(false);
      if (
        minimumBoardRevision === undefined &&
        revisionFreeReconcile.current !== null
      ) {
        return revisionFreeReconcile.current;
      }
      const reconcile = async () => {
        const facilityGeneration = refreshGeneration.current;
        try {
          return await refresh(
            facilityId,
            facilityGeneration,
            minimumBoardRevision,
          );
        } catch (err) {
          if (
            facilityGeneration === refreshGeneration.current &&
            facilityId === currentFacilityId.current
          ) {
            setError(
              err instanceof Error && err.message === BOARD_UNAVAILABLE_MESSAGE
                ? null
                : err instanceof Error
                  ? err.message
                  : "Failed to refresh the queue.",
            );
          }
          throw err;
        }
      };
      if (minimumBoardRevision !== undefined) return reconcile();

      const pending = reconcile().finally(() => {
        if (revisionFreeReconcile.current === pending) {
          revisionFreeReconcile.current = null;
        }
      });
      revisionFreeReconcile.current = pending;
      return pending;
    },
    [facilityId, refresh],
  );
  useEffect(() => {
    if (
      requestedRefreshGeneration === handledHostRefreshGeneration.current ||
      !facilityId ||
      loadedFacilityId !== facilityId
    ) {
      return;
    }
    handledHostRefreshGeneration.current = requestedRefreshGeneration;
    void reconcileCourtState(0).catch(() => undefined);
  }, [
    facilityId,
    loadedFacilityId,
    reconcileCourtState,
    requestedRefreshGeneration,
  ]);
  const markCourtStateRevisionPending = useCallback(
    (revision: number) => {
      if (!facilityId || facilityId !== currentFacilityId.current) return;
      pendingBoardRevision.current = Math.max(
        pendingBoardRevision.current ?? 0,
        revision,
      );
      ++refreshRequestId.current;
      setSnapshotCurrent(false);
      setRefreshInFlight(true);
    },
    [facilityId],
  );
  useCourtStateStream({
    facilityId: facilityId ?? "",
    revision: boardFacilityId === facilityId ? (board?.revision ?? 0) : 0,
    onReconcile: reconcileCourtState,
    onRevisionPending: markCourtStateRevisionPending,
    enabled: Boolean(facilityId),
  });
  const markReservationStateRevisionPending = useCallback(() => {
    if (!facilityId || facilityId !== currentFacilityId.current) return;
    ++refreshRequestId.current;
    setSnapshotCurrent(false);
    setRefreshInFlight(true);
    setManualCourtSelections({});
    if (assignmentConfirmationRef.current !== null) {
      setAssignmentConfirmation(null);
      setError("Court availability changed. Review the refreshed courts.");
    }
  }, [facilityId]);
  const reconcileReservationState = useCallback(
    (_reservationRevision?: number) => reconcileCourtState(undefined),
    [reconcileCourtState],
  );
  useReservationStateStream({
    facilityId: facilityId ?? "",
    revision: 0,
    onReconcile: reconcileReservationState,
    onRevisionPending: markReservationStateRevisionPending,
    enabled: Boolean(facilityId),
  });

  // Derived KPIs — all from real backend fields. Live court availability and
  // occupancy are board-only facts: queue stats cannot distinguish an active
  // game from a closed or reserved court, so they are not a safe fallback.
  const hasQueueSnapshot = stats !== null;
  const waiting = hasQueueSnapshot ? (stats.queue_size ?? queue.length) : null;
  const hasBoardSnapshot = board !== null;
  const hasDisplayableBoard =
    hasBoardSnapshot &&
    boardError === null &&
    boardFacilityId === facilityId &&
    (snapshotCurrent || refreshInFlight);
  const hasCurrentBoard = hasDisplayableBoard && snapshotCurrent;
  const queueOpen = hasCurrentBoard ? (board?.queue_open ?? null) : null;
  const checkInActive = hasCurrentBoard ? (board?.active_now ?? null) : null;
  const queueStateLabel =
    queueOpen === false
      ? "Queue closed"
      : queueOpen === true && checkInActive === true
        ? "Accepting check-ins"
        : queueOpen === true && checkInActive === false
          ? "Check-in closed"
          : "Queue state unavailable";
  const currentBoardCourts = hasCurrentBoard ? (board?.courts ?? []) : null;
  const availableCourts =
    currentBoardCourts?.filter(isCourtReadyToAssign).length ?? null;
  const inPlay =
    currentBoardCourts?.filter((entry) => entry.occupancy_state === "in_play")
      .length ?? null;
  const avgWaitMin = useMemo<number | null>(() => {
    if (!hasQueueSnapshot) return null;
    if (stats?.avg_wait_minutes != null)
      return Math.round(stats.avg_wait_minutes);
    return null;
  }, [hasQueueSnapshot, stats]);
  const freshAssignmentCount =
    hasQueueSnapshot && Number.isInteger(stats.fresh_assignment_count)
      ? stats.fresh_assignment_count
      : null;
  const topOffAssignmentCount =
    hasQueueSnapshot && Number.isInteger(stats.top_off_assignment_count)
      ? stats.top_off_assignment_count
      : null;
  // QueueStats is refreshed with the live queue snapshot, so another operator's
  // mode change updates this action matrix without a page reload.
  const canManuallyCall =
    snapshotCurrent &&
    !refreshInFlight &&
    stats?.queue_assignment_mode === "manual";
  const canAddWalkIn =
    snapshotCurrent &&
    !refreshInFlight &&
    stats !== null &&
    stats.queue_assignment_mode !== "self_serve";
  const actionsCurrent = snapshotCurrent && !refreshInFlight;
  const boardByCourtId = useMemo(
    () =>
      new Map((board?.courts ?? []).map((entry) => [entry.court_id, entry])),
    [board],
  );
  const availabilityByCourtId = useMemo(
    () =>
      new Map(
        (courtAvailability?.courts ?? []).map((verdict) => [
          verdict.court_id,
          verdict,
        ]),
      ),
    [courtAvailability],
  );
  const currentOnCourt = useMemo(
    () => (hasCurrentBoard ? onCourt : []),
    [hasCurrentBoard, onCourt],
  );
  const onCourtByCourtId = useMemo(
    () => new Map(currentOnCourt.map((entry) => [entry.courtId, entry])),
    [currentOnCourt],
  );
  const activeBoardCourtIds = useMemo(
    () =>
      hasCurrentBoard
        ? new Set(
            (board?.courts ?? [])
              .filter((entry) => entry.occupancy_state === "in_play")
              .map((entry) => entry.court_id),
          )
        : new Set<string>(),
    [board, hasCurrentBoard],
  );

  useEffect(() => {
    if (!actionsCurrent) return;
    if (
      waitingRecovery &&
      !queue.some((entry) => entry.id === waitingRecovery.entryId)
    ) {
      setWaitingRecovery(null);
    }
    if (
      reassignAction &&
      !currentOnCourt.some(
        (entry) =>
          entry.courtId === reassignAction.sourceCourtId &&
          entry.playingGroups.some(
            (group) => group.assignmentId === reassignAction.group.assignmentId,
          ),
      )
    ) {
      setReassignAction(null);
      setReassignPreview(null);
      setReassignCourtId("");
    }
    if (endSessionCourt && !activeBoardCourtIds.has(endSessionCourt.id)) {
      setEndSessionCourt(null);
    }
  }, [
    activeBoardCourtIds,
    actionsCurrent,
    currentOnCourt,
    endSessionCourt,
    queue,
    reassignAction,
    waitingRecovery,
  ]);

  const filteredQueue = useMemo(
    () =>
      queue.filter((q) => {
        if (!search.trim()) return true;
        const term = search.trim().toLowerCase();
        // Match on the position label (always) and resolved display name (when present).
        if (partyLabel(q).toLowerCase().includes(term)) return true;
        if (
          isResolvedName(q.displayName, q.leadUserId) &&
          q.displayName!.toLowerCase().includes(term)
        )
          return true;
        return false;
      }),
    [queue, search],
  );

  function availabilityStatusText(
    verdict: CourtAvailabilityCourtVerdict | undefined,
  ): string {
    if (!verdict) return "Availability unavailable";
    const conflict = verdict.conflicts[0];
    if (verdict.unavailable_reason === "court_disabled")
      return "Court disabled";
    if (verdict.unavailable_reason === "outside_operating_hours")
      return "Outside operating hours";
    if (verdict.unavailable_reason === "court_conflict") {
      if (!conflict) return "Reserved";
      const descriptors = [conflict.event_type, conflict.source_type ?? ""];
      const describes = (token: string) =>
        descriptors.some((descriptor) => descriptor.includes(token));
      const label = describes("queue_assignment")
        ? "In play"
        : describes("maintenance") || describes("staff_block")
          ? "Maintenance"
          : describes("closed") || describes("closure")
            ? "Closed"
            : describes("program") || describes("class")
              ? "Program"
              : describes("league")
                ? "League"
                : describes("tournament")
                  ? "Tournament"
                  : "Reserved";
      if (
        courtAvailability?.start_time &&
        Date.parse(conflict.start_time) >
          Date.parse(courtAvailability.start_time)
      ) {
        const upcomingLabel = label === "Reserved" ? "Reservation" : label;
        return `${upcomingLabel} at ${formatFacilityTime(
          conflict.start_time,
          courtAvailability.timezone,
          true,
        )}`;
      }
      return `${label} until ${formatFacilityTime(
        conflict.end_time,
        courtAvailability?.timezone ?? "UTC",
        true,
      )}`;
    }
    if (verdict.available_until) {
      const requestedEnd = courtAvailability?.end_time;
      if (
        requestedEnd &&
        Date.parse(verdict.available_until) < Date.parse(requestedEnd)
      ) {
        return `Available until ${formatFacilityTime(
          verdict.available_until,
          courtAvailability.timezone,
          true,
        )}`;
      }
    }
    return "Available";
  }

  function manualCourtOptions(entry: QueueEntry): ManualCourtOption[] {
    const requiredCapacity = entry.groupSize ?? 1;
    return courts.map((court) => {
      const boardEntry = boardByCourtId.get(court.id);
      const verdict = availabilityByCourtId.get(court.id);
      const capacity = boardEntry?.capacity ?? court.maxPlayers;
      let status = availabilityStatusText(verdict);
      let available =
        hasCurrentBoard &&
        courtAvailabilityStatus === "ready" &&
        verdict?.available === true &&
        boardEntry?.availability_state === "assignable";
      if (!boardEntry) {
        available = false;
        status = "Court state unavailable";
      } else if (boardEntry.availability_state !== "assignable") {
        available = false;
        if (!verdict || verdict.available) {
          status = getCourtAssignmentLabel(boardEntry);
          if (
            boardEntry.operational_state === "operational" &&
            boardEntry.occupancy_state === "in_play" &&
            boardEntry.is_private
          ) {
            status = "In play, private game";
          }
        }
      } else if (capacity != null && capacity < requiredCapacity) {
        available = false;
        status = `Capacity ${capacity}, party ${requiredCapacity}`;
      }
      return {
        court,
        available,
        status,
        capacity: capacity ?? null,
        availableUntil: verdict?.available_until ?? null,
      };
    });
  }

  async function callToSelectedCourt(
    entry: QueueEntry,
    courtId: string,
    requestedDurationMinutes: number,
  ) {
    // Revalidate the explicit staff choice against the latest authoritative
    // board before issuing the assignment.
    const selectedOption = manualCourtOptions(entry).find(
      (option) => option.court.id === courtId && option.available,
    );
    const operation = captureFacilityOperation();
    if (!operation) return;
    if (!selectedOption) {
      setManualCourtSelections((selections) => {
        const next = { ...selections };
        delete next[entry.id];
        return next;
      });
      setAssignmentConfirmation(null);
      setError("Court availability changed. Review the refreshed courts.");
      return;
    }
    setBusyAction(`call-${entry.id}`);
    setError(null);
    try {
      const settlement = await settleFacilityAction(operation, () =>
        assignCourt(
          entry.id,
          selectedOption.court.id,
          requestedDurationMinutes,
        ),
      );
      if (!settlement.current) return;
      if (settlement.error) {
        setManualCourtSelections((selections) => {
          const next = { ...selections };
          delete next[entry.id];
          return next;
        });
        setError(
          `${actionErrorMessage(
            settlement.error,
            "Failed to assign party",
          )} Court availability has been refreshed.`,
        );
      }
      if (settlement.mutationSucceeded && settlement.reconciliationCompleted) {
        setManualCourtSelections((selections) => {
          const next = { ...selections };
          delete next[entry.id];
          return next;
        });
      }
      setAssignmentConfirmation(null);
    } finally {
      if (isCurrentFacilityOperation(operation)) setBusyAction(null);
    }
  }

  async function reviewCourtAssignment(
    entry: QueueEntry,
    entryLabel: string,
    selectedOption: ManualCourtOption,
  ) {
    const operation = captureFacilityOperation();
    if (!operation || !selectedOption.available) return;
    const requestedDurationMinutes = queueSessionDurationMinutes.current;
    const startsAt = new Date();
    const endsAt = new Date(
      startsAt.getTime() + requestedDurationMinutes * 60_000,
    );
    setBusyAction(`preview-${entry.id}`);
    setError(null);
    try {
      const preview = await previewCourtAvailability(operation.facilityId, {
        court_ids: [selectedOption.court.id],
        start_time: startsAt.toISOString(),
        end_time: endsAt.toISOString(),
        minimum_duration_minutes: queueMinimumRemainingMinutes.current,
      });
      if (!isCurrentFacilityOperation(operation)) return;
      const verdict = preview.courts[0];
      const stillOpen = manualCourtOptions(entry).some(
        (option) =>
          option.court.id === selectedOption.court.id && option.available,
      );
      if (!verdict?.available || !verdict.available_until || !stillOpen) {
        throw new Error("The selected court is no longer available.");
      }
      const assignmentDurationMinutes = Math.floor(
        (Date.parse(verdict.available_until) - Date.parse(preview.start_time)) /
          60_000,
      );
      if (assignmentDurationMinutes < queueMinimumRemainingMinutes.current) {
        throw new Error(
          "The selected court no longer has a full playable block.",
        );
      }
      setAssignmentConfirmation({
        entry,
        entryLabel,
        court: selectedOption.court,
        startsAt: preview.start_time,
        endsAt: verdict.available_until,
        assignmentDurationMinutes,
        timezone: preview.timezone,
      });
    } catch (error) {
      if (!isCurrentFacilityOperation(operation)) return;
      setManualCourtSelections((selections) => {
        const next = { ...selections };
        delete next[entry.id];
        return next;
      });
      setAssignmentConfirmation(null);
      try {
        await refresh(operation.facilityId, operation.generation);
      } catch {
        // The original preview error remains the actionable operator message.
      }
      if (isCurrentFacilityOperation(operation)) {
        setError(
          `${actionErrorMessage(
            error,
            "Court availability could not be confirmed.",
          )} Court availability has been refreshed.`,
        );
      }
    } finally {
      if (isCurrentFacilityOperation(operation)) setBusyAction(null);
    }
  }

  function openWaitingRecovery(action: WaitingRecoveryAction) {
    if (!actionsCurrent) return;
    setWaitingRecovery(action);
    setRecoveryReasonCode("");
    setRecoveryNote("");
    setRecoveryDialogError(null);
  }

  function openEndSession(court: { id: string; label: string }) {
    if (!actionsCurrent) return;
    setEndSessionCourt(court);
    setRecoveryReasonCode("");
    setRecoveryNote("");
    setRecoveryDialogError(null);
  }

  function recoveryReasonsFor(action: RecoveryAction) {
    return recoveryReasons.filter((reason) => reason.actions.includes(action));
  }

  function recoveryBody(): QueueOperatorAction | null {
    if (!recoveryReasonCode) return null;
    return {
      reason_code: recoveryReasonCode,
      note: recoveryNote.trim() || null,
    };
  }

  function reassignCourtOptions(): ManualCourtOption[] {
    if (!reassignAction || !reassignPreview) return [];
    const requiredUntil = Date.parse(reassignAction.group.expiresAt);
    const requiredCapacity = reassignAction.group.groupSize;
    return courts
      .filter((court) => court.id !== reassignAction.sourceCourtId)
      .map((court) => {
        const boardEntry = boardByCourtId.get(court.id);
        const verdict = reassignPreview.courts.find(
          (candidate) => candidate.court_id === court.id,
        );
        const safeUntil = verdict?.available_until
          ? Date.parse(verdict.available_until)
          : Number.NaN;
        const capacity =
          verdict?.max_players ??
          boardByCourtId.get(court.id)?.capacity ??
          court.maxPlayers;
        const fitsParty = capacity == null || capacity >= requiredCapacity;
        const available =
          boardEntry?.availability_state === "assignable" &&
          verdict?.available === true &&
          Number.isFinite(safeUntil) &&
          safeUntil >= requiredUntil &&
          fitsParty;
        return {
          court,
          available,
          status: !fitsParty
            ? `Needs ${requiredCapacity} players`
            : boardEntry?.availability_state !== "assignable"
              ? boardEntry
                ? getCourtAssignmentLabel(boardEntry)
                : "Court state unavailable"
              : available
                ? `Available through ${formatFacilityTime(
                    reassignAction.group.expiresAt,
                    reassignPreview.timezone,
                    true,
                  )}`
                : verdict?.available_until
                  ? `Available only until ${formatFacilityTime(
                      verdict.available_until,
                      reassignPreview.timezone,
                      true,
                    )}`
                  : (verdict?.unavailable_reason ?? "Unavailable"),
          capacity,
          availableUntil: verdict?.available_until ?? null,
        };
      });
  }

  async function loadReassignPreview(action: ReassignAction) {
    const operation = captureFacilityOperation();
    if (!operation) return;
    const previewRequestId = ++reassignPreviewRequestId.current;
    const isCurrentPreview = () =>
      isCurrentFacilityOperation(operation) &&
      previewRequestId === reassignPreviewRequestId.current;
    const destinationCourtIds = courts
      .filter(
        (court) =>
          court.id !== action.sourceCourtId &&
          boardByCourtId.get(court.id)?.availability_state === "assignable",
      )
      .map((court) => court.id);
    const startsAt = new Date();
    const expiresAt = new Date(action.group.expiresAt);
    const remainingMinutes = Math.ceil(
      (expiresAt.getTime() - startsAt.getTime()) / 60_000,
    );
    if (remainingMinutes <= 0 || destinationCourtIds.length === 0) {
      setReassignPreview(null);
      setRecoveryDialogError(
        remainingMinutes <= 0
          ? "This assignment no longer has an active timer."
          : "No other enabled courts are available for reassignment.",
      );
      return;
    }

    setReassignBusy(true);
    setRecoveryDialogError(null);
    try {
      const preview = await previewCourtAvailability(operation.facilityId, {
        court_ids: destinationCourtIds,
        start_time: startsAt.toISOString(),
        end_time: action.group.expiresAt,
        minimum_duration_minutes: Math.max(
          1,
          Math.min(queueMinimumRemainingMinutes.current, remainingMinutes),
        ),
      });
      if (!isCurrentPreview()) return;
      setReassignPreview(preview);
    } catch (error) {
      if (!isCurrentPreview()) return;
      setReassignPreview(null);
      setRecoveryDialogError(
        actionErrorMessage(
          error,
          "Destination court availability could not be loaded.",
        ),
      );
    } finally {
      if (isCurrentPreview()) setReassignBusy(false);
    }
  }

  function openReassign(action: ReassignAction) {
    if (!actionsCurrent) return;
    setReassignAction(action);
    setReassignPreview(null);
    setReassignCourtId("");
    setRecoveryReasonCode("");
    setRecoveryNote("");
    setRecoveryDialogError(null);
    void loadReassignPreview(action);
  }

  async function runReassign() {
    const action = reassignAction;
    const operation = captureFacilityOperation();
    const body = recoveryBody();
    const destination = reassignCourtOptions().find(
      (option) => option.court.id === reassignCourtId && option.available,
    );
    const sourceStillCurrent =
      action !== null &&
      currentOnCourt.some(
        (entry) =>
          entry.courtId === action.sourceCourtId &&
          entry.playingGroups.some(
            (group) => group.assignmentId === action.group.assignmentId,
          ),
      );
    if (
      !action ||
      !operation ||
      !body ||
      !destination ||
      !actionsCurrent ||
      !sourceStillCurrent
    )
      return;

    setReassignBusy(true);
    setRecoveryDialogError(null);
    try {
      const settlement = await settleFacilityAction(operation, () =>
        reassignCourt(action.group.assignmentId, {
          ...body,
          destination_court_id: destination.court.id,
        }),
      );
      if (!settlement.current) return;
      if (settlement.error) {
        setRecoveryDialogError(
          actionErrorMessage(
            settlement.error,
            `${destination.court.label} is no longer available.`,
          ),
        );
        setReassignCourtId("");
        await loadReassignPreview(action);
      }
      if (settlement.mutationSucceeded && settlement.reconciliationCompleted) {
        setReassignAction(null);
      }
    } finally {
      if (isCurrentFacilityOperation(operation)) setReassignBusy(false);
    }
  }

  async function runWaitingRecovery() {
    const recovery = waitingRecovery;
    const operation = captureFacilityOperation();
    const body = recoveryBody();
    const entryStillWaiting =
      recovery !== null && queue.some((entry) => entry.id === recovery.entryId);
    if (
      !operation ||
      !recovery ||
      !body ||
      !actionsCurrent ||
      !entryStillWaiting
    )
      return;
    setBusyAction(`${recovery.kind}-${recovery.entryId}`);
    setError(null);
    setRecoveryDialogError(null);
    try {
      const settlement = await settleFacilityAction(operation, () =>
        recovery.kind === "skip"
          ? skipWaitingEntry(recovery.entryId, body)
          : removeWaitingEntry(recovery.entryId, body),
      );
      if (settlement.current && settlement.error) {
        setRecoveryDialogError(
          actionErrorMessage(
            settlement.error,
            recovery.kind === "skip"
              ? "Failed to skip party"
              : "Failed to remove party",
          ),
        );
      }
      if (settlement.mutationSucceeded && settlement.reconciliationCompleted) {
        setWaitingRecovery(null);
      }
    } finally {
      if (isCurrentFacilityOperation(operation)) setBusyAction(null);
    }
  }

  // Confirmed end-session frees the court so the queue can refill.
  async function runEndSession() {
    const court = endSessionCourt;
    const operation = captureFacilityOperation();
    const body = recoveryBody();
    const boardCourt = court ? boardByCourtId.get(court.id) : null;
    const isActiveCourt = boardCourt?.occupancy_state === "in_play";
    const courtStillCurrent =
      court !== null && activeBoardCourtIds.has(court.id);
    if (
      !court ||
      !operation ||
      !body ||
      !actionsCurrent ||
      !isActiveCourt ||
      !courtStillCurrent
    ) {
      setEndSessionCourt(null);
      return;
    }
    setEndSessionBusy(true);
    setError(null);
    try {
      const settlement = await settleFacilityAction(operation, () =>
        endSession(court.id, operation.facilityId, body),
      );
      if (!settlement.current) return;
      if (settlement.error) {
        setRecoveryDialogError(
          actionErrorMessage(settlement.error, "Failed to end the session"),
        );
      }
      if (settlement.mutationSucceeded && settlement.reconciliationCompleted) {
        setEndSessionCourt(null);
      }
    } finally {
      if (isCurrentFacilityOperation(operation)) setEndSessionBusy(false);
    }
  }

  async function toggleMaintenance(courtId: string) {
    const court = courts.find((c) => c.id === courtId);
    const boardCourt = boardByCourtId.get(courtId);
    const operation = captureFacilityOperation();
    if (
      !court ||
      !boardCourt ||
      boardCourt.operational_state === "closed" ||
      !operation ||
      !actionsCurrent
    )
      return;
    const nextStatus: CourtStatus =
      boardCourt.operational_state === "maintenance"
        ? "enabled"
        : "maintenance";
    setBusyAction(`court-${courtId}`);
    setError(null);
    try {
      const settlement = await settleFacilityAction(operation, () =>
        updateCourt(courtId, { status: nextStatus }),
      );
      if (settlement.current && settlement.error) {
        setError(
          actionErrorMessage(settlement.error, "Failed to update court"),
        );
      }
    } finally {
      if (isCurrentFacilityOperation(operation)) setBusyAction(null);
    }
  }

  async function addWalkIn() {
    const operation = captureFacilityOperation();
    if (!operation || !canAddWalkIn) {
      setWalkInError("No facility loaded yet.");
      return;
    }
    if (!walkInCustomer) {
      setWalkInError("Select a customer first.");
      return;
    }
    const mode = walkInMode;
    setWalkInSaving(true);
    setWalkInError(null);
    try {
      const settlement = await settleFacilityAction(operation, () =>
        addWalkInParty({
          facility_id: operation.facilityId,
          player_user_id: walkInCustomer.userId,
          mode,
        }),
      );
      if (!settlement.current) return;
      if (settlement.error) {
        setWalkInError(
          actionErrorMessage(settlement.error, "Failed to add walk-in"),
        );
      }
      if (settlement.mutationSucceeded && settlement.reconciliationCompleted) {
        setWalkInCustomer(null);
        setWalkInOpen(false);
      }
    } finally {
      if (isCurrentFacilityOperation(operation)) setWalkInSaving(false);
    }
  }

  async function runConfirmAction() {
    const action = confirmAction;
    if (!action || !actionsCurrent) return;
    if (
      (action === "close" && queueOpen !== true) ||
      (action === "reopen" && queueOpen !== false)
    ) {
      setConfirmAction(null);
      return;
    }
    const operation = captureFacilityOperation();
    if (!operation) {
      setError("No facility loaded yet.");
      setConfirmAction(null);
      return;
    }
    setConfirmBusy(true);
    setError(null);
    try {
      const settlement = await settleFacilityAction(operation, async () => {
        if (action === "close") await closeFacilityQueue(operation.facilityId);
        else if (action === "reopen")
          await reopenFacilityQueue(operation.facilityId);
        else await clearFacilityQueue(operation.facilityId);
      });
      if (!settlement.current) return;
      if (settlement.error) {
        setError(
          actionErrorMessage(settlement.error, "Failed to update queue"),
        );
      }
      if (settlement.mutationSucceeded && settlement.reconciliationCompleted) {
        setConfirmAction(null);
      }
    } finally {
      if (isCurrentFacilityOperation(operation)) setConfirmBusy(false);
    }
  }

  const CONFIRM_COPY: Record<
    "close" | "reopen" | "clear",
    {
      title: string;
      description: string;
      confirmLabel: string;
      busyLabel: string;
      destructive: boolean;
    }
  > = {
    close: {
      title: "Close the queue?",
      description:
        "Players will no longer be able to join the drop-in queue until you reopen it. Parties already waiting are not removed.",
      confirmLabel: "Close queue",
      busyLabel: "Closing…",
      destructive: false,
    },
    reopen: {
      title: "Reopen the queue?",
      description: "Players will be able to join the drop-in queue again.",
      confirmLabel: "Reopen queue",
      busyLabel: "Reopening…",
      destructive: false,
    },
    clear: {
      title: "Clear the waiting queue?",
      description:
        "This removes every waiting party from the queue. This cannot be undone.",
      confirmLabel: "Clear waiting queue",
      busyLabel: "Clearing…",
      destructive: true,
    },
  };
  const confirmActionMatchesSnapshot =
    confirmAction === "clear" ||
    (confirmAction === "close" && queueOpen === true) ||
    (confirmAction === "reopen" && queueOpen === false);

  if (facilityLoading || !facilityId || loadedFacilityId !== facilityId) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          minHeight: "40vh",
          display: "grid",
          placeItems: "center",
          px: { xs: 2, lg: 3 },
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      {embedded ? null : (
        <Box>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
            Queue & courts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {facility?.name ?? "Selected facility"}
          </Typography>
        </Box>
      )}

      <Paper
        component="section"
        variant="outlined"
        aria-label="Queue status and controls"
        sx={{ p: 1.5 }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Stack
              direction="row"
              useFlexGap
              sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.5 }}
            >
              <Chip
                role="status"
                aria-label={queueStateLabel}
                aria-live="polite"
                size="small"
                variant="outlined"
                color={queueOpen === false ? "default" : "success"}
                icon={
                  queueOpen === true && checkInActive === true ? (
                    <LockOpenIcon aria-hidden="true" />
                  ) : (
                    <LockIcon aria-hidden="true" />
                  )
                }
                label={queueStateLabel}
              />
              <StatusMetric
                label="Waiting"
                value={waiting == null ? "—" : String(waiting)}
                detail={
                  waiting == null
                    ? "Queue status unknown"
                    : waiting === 1
                      ? "party"
                      : "parties"
                }
              />
              <StatusMetric
                label="In play"
                value={inPlay == null ? "—" : String(inPlay)}
                detail={inPlay == null ? "Live status unknown" : "courts"}
              />
              <StatusMetric
                label="Available courts"
                value={availableCourts == null ? "—" : String(availableCourts)}
                detail={
                  availableCourts == null
                    ? "Availability unknown"
                    : "ready to assign"
                }
              />
              <StatusMetric
                label="Recent avg wait"
                value={avgWaitMin == null ? "No data" : `${avgWaitMin}m`}
                detail={
                  avgWaitMin == null
                    ? "No recent completed players"
                    : "completed players, last 2h"
                }
              />
            </Stack>
            <Stack
              direction="row"
              useFlexGap
              sx={{ flexWrap: "wrap", flexShrink: 0, gap: 1 }}
            >
              {queueOpen === true ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LockIcon />}
                  onClick={() => setConfirmAction("close")}
                  disabled={
                    confirmBusy || busyAction !== null || !actionsCurrent
                  }
                >
                  Close queue
                </Button>
              ) : queueOpen === false ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LockOpenIcon />}
                  onClick={() => setConfirmAction("reopen")}
                  disabled={
                    confirmBusy || busyAction !== null || !actionsCurrent
                  }
                >
                  Reopen queue
                </Button>
              ) : null}
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmAction("clear")}
                disabled={
                  busyAction !== null ||
                  confirmBusy ||
                  !actionsCurrent ||
                  waiting == null ||
                  waiting === 0
                }
              >
                Clear waiting queue
              </Button>
            </Stack>
          </Stack>
          <Divider />
          <Stack
            direction="row"
            useFlexGap
            aria-label="Assignment mix, last 2 hours"
            sx={{ flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              {freshAssignmentCount === null ? "—" : freshAssignmentCount} fresh
              assignments
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {topOffAssignmentCount === null ? "—" : topOffAssignmentCount}{" "}
              active-court fill-ins
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last 2 hours
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {error && !confirmAction ? (
        <Alert severity="error" aria-live="assertive">
          {error}
        </Alert>
      ) : null}

      {boardError && !confirmAction ? (
        <Alert severity="error" aria-live="assertive">
          {boardError}
        </Alert>
      ) : null}

      {recoveryCatalogError ? (
        <Alert severity="error" aria-label="Operator recovery unavailable">
          Operator recovery unavailable. {recoveryCatalogError}
        </Alert>
      ) : null}

      <Box sx={{ display: { xs: "block", lg: "none" } }}>
        <Tabs
          value={workspaceView}
          onChange={(_event, value: "queue" | "courts") =>
            setWorkspaceView(value)
          }
          aria-label="Drop-in workspace view"
          variant="fullWidth"
        >
          <Tab
            id="queue-workspace-queue-tab"
            value="queue"
            label="Waiting"
            aria-controls="queue-workspace-queue-panel"
          />
          <Tab
            id="queue-workspace-courts-tab"
            value="courts"
            label="Courts"
            aria-controls="queue-workspace-courts-panel"
          />
        </Tabs>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1fr) minmax(340px, 420px)",
          },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          component="section"
          variant="outlined"
          id="queue-workspace-queue-panel"
          role="tabpanel"
          aria-labelledby="queue-workspace-queue-tab"
          data-testid="queue-workspace-queue"
          hidden={compactWorkspace && workspaceView !== "queue"}
          sx={(theme) => ({
            minWidth: 0,
            display: "block",
            [theme.breakpoints.down("lg")]: {
              display: workspaceView === "queue" ? "block" : "none",
            },
          })}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { sm: "center" },
              justifyContent: "space-between",
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography component="h2" variant="h6">
              Waiting queue
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <TextField
                id="queue-search"
                type="search"
                size="small"
                placeholder="Find a party…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ width: { xs: "100%", sm: 224 }, maxWidth: "100%" }}
                slotProps={{
                  htmlInput: { "aria-label": "Search queue" },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" aria-hidden="true" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {canAddWalkIn ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setWalkInMode("normal");
                    setWalkInCustomer(null);
                    setWalkInError(null);
                    setWalkInOpen(true);
                  }}
                  disabled={busyAction !== null}
                >
                  Add walk-in
                </Button>
              ) : null}
            </Stack>
          </Stack>
          <TableContainer sx={{ overflowX: "auto", maxHeight: 520 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }}>#</TableCell>
                  <TableCell>Party</TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    Joined
                  </TableCell>
                  <TableCell>Line age</TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Format
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQueue.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 4, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        {loading
                          ? "Loading…"
                          : !hasQueueSnapshot
                            ? "Queue data unavailable."
                            : search.trim()
                              ? "No parties match your search."
                              : "No one is waiting in the queue right now."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredQueue.map((entry, idx) => {
                    const wait = minutesAgo(entry.joinedAt);
                    const label = partyLabel(entry);
                    const resolved = isResolvedName(
                      entry.displayName,
                      entry.leadUserId,
                    );
                    const actionLabel = resolved ? entry.displayName! : label;
                    const courtOptions = manualCourtOptions(entry);
                    const selectedCourtId = courtOptions.find(
                      (option) =>
                        option.available &&
                        option.court.id === manualCourtSelections[entry.id],
                    )
                      ? manualCourtSelections[entry.id]
                      : "";
                    const selectedCourtOption = courtOptions.find(
                      (option) => option.court.id === selectedCourtId,
                    );
                    const companions =
                      entry.groupSize != null && entry.groupSize > 1
                        ? entry.groupSize - 1
                        : null;
                    const targetCourtLabel = entry.targetCourtId
                      ? courts.find((court) => court.id === entry.targetCourtId)
                          ?.label
                      : null;
                    const policyCopy = waitingPolicyCopy(entry);
                    return (
                      <TableRow key={entry.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center", minWidth: 0 }}
                          >
                            <Avatar
                              src={
                                entry.avatarUrl && resolved
                                  ? entry.avatarUrl
                                  : undefined
                              }
                              alt={
                                entry.avatarUrl && resolved
                                  ? entry.displayName!
                                  : ""
                              }
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: "0.875rem",
                              }}
                            >
                              {resolved
                                ? entry.displayName!.charAt(0)
                                : label.charAt(0)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              {resolved ? (
                                <Stack
                                  direction="row"
                                  useFlexGap
                                  sx={{
                                    minWidth: 0,
                                    alignItems: "baseline",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{
                                      minWidth: 0,
                                      overflowWrap: "anywhere",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {entry.displayName}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      flexShrink: 0,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {playerHandleSuffix(entry.leadUserId!)}
                                  </Typography>
                                  {companions != null && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      +{companions}
                                    </Typography>
                                  )}
                                </Stack>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {label}
                                </Typography>
                              )}
                              {entry.targetCourtId ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  Waiting for{" "}
                                  {targetCourtLabel ?? "selected court"}
                                </Typography>
                              ) : null}
                              {policyCopy ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  {policyCopy}
                                </Typography>
                              ) : null}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{
                            display: { xs: "none", sm: "table-cell" },
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {entry.joinedAt.toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={fmtMin(wait)}
                            color={
                              wait > 20
                                ? "error"
                                : wait > 10
                                  ? "warning"
                                  : "default"
                            }
                            variant={wait > 10 ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            display: { xs: "none", md: "table-cell" },
                            textTransform: "capitalize",
                          }}
                        >
                          {entry.preference ?? "—"}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            useFlexGap
                            sx={{
                              alignItems: "center",
                              justifyContent: "flex-end",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            {canManuallyCall ? (
                              <>
                                <ManualCourtPicker
                                  entryLabel={actionLabel}
                                  options={courtOptions}
                                  value={selectedCourtId}
                                  onValueChange={(courtId) =>
                                    setManualCourtSelections((selections) => ({
                                      ...selections,
                                      [entry.id]: courtId,
                                    }))
                                  }
                                  disabled={
                                    courtOptions.length === 0 ||
                                    busyAction !== null
                                  }
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  endIcon={<ArrowForwardIcon />}
                                  aria-label={`Review assignment for ${actionLabel}`}
                                  onClick={() => {
                                    if (!selectedCourtOption?.available) return;
                                    void reviewCourtAssignment(
                                      entry,
                                      actionLabel,
                                      selectedCourtOption,
                                    );
                                  }}
                                  disabled={
                                    !selectedCourtId || busyAction !== null
                                  }
                                  title={
                                    !hasCurrentBoard
                                      ? "Court availability unavailable"
                                      : courtAvailabilityStatus === "error"
                                        ? "Court availability unavailable"
                                        : selectedCourtId
                                          ? `Review assignment to ${selectedCourtOption?.court.label ?? "selected court"}`
                                          : courtOptions.some(
                                                (option) => option.available,
                                              )
                                            ? "Choose a court first"
                                            : "No available court fits this party"
                                  }
                                >
                                  Review
                                </Button>
                              </>
                            ) : null}
                            <WaitingActionsMenu
                              actionLabel={actionLabel}
                              disabled={
                                busyAction !== null ||
                                !actionsCurrent ||
                                recoveryReasons.length === 0
                              }
                              disabledReason={recoveryCatalogError}
                              canSkip={recoveryReasonsFor("skip").length > 0}
                              canRemove={
                                recoveryReasonsFor("remove").length > 0
                              }
                              onSkip={() =>
                                openWaitingRecovery({
                                  kind: "skip",
                                  entryId: entry.id,
                                  label: actionLabel,
                                })
                              }
                              onRemove={() =>
                                openWaitingRecovery({
                                  kind: "remove",
                                  entryId: entry.id,
                                  label: actionLabel,
                                })
                              }
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box
          component="section"
          id="queue-workspace-courts-panel"
          role="tabpanel"
          aria-labelledby="queue-workspace-courts-tab"
          data-testid="queue-workspace-courts"
          hidden={compactWorkspace && workspaceView !== "courts"}
          sx={(theme) => ({
            minWidth: 0,
            display: "block",
            [theme.breakpoints.down("lg")]: {
              display: workspaceView === "courts" ? "block" : "none",
            },
          })}
        >
          <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>
            Court board
          </Typography>
          {courts.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 4, textAlign: "center" }}
            >
              {loading
                ? "Loading…"
                : !hasQueueSnapshot
                  ? "Court list unavailable."
                  : "No courts configured yet."}
            </Typography>
          ) : renderCourtTiles ? (
            <Box
              role="region"
              aria-label="Court board list"
              tabIndex={compactWorkspace ? undefined : 0}
              sx={{
                maxHeight: compactWorkspace ? "none" : 520,
                overflowY: compactWorkspace ? "visible" : "auto",
                pr: compactWorkspace ? 0 : 0.5,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: compactWorkspace
                    ? "repeat(2, minmax(0, 1fr))"
                    : "minmax(0, 1fr)",
                  gap: 1,
                }}
              >
                {courts.map((court) => {
                  const boardEntry = hasDisplayableBoard
                    ? (boardByCourtId.get(court.id) ?? null)
                    : null;
                  const onCourtEntry = onCourtByCourtId.get(court.id) ?? null;
                  return (
                    <CourtTile
                      key={court.id}
                      court={court}
                      boardEntry={boardEntry}
                      onCourtEntry={onCourtEntry}
                      disabled={busyAction !== null || !actionsCurrent}
                      canManageCourts={canManageCourts}
                      onToggleMaintenance={() => toggleMaintenance(court.id)}
                      onEndSession={
                        boardEntry?.occupancy_state === "in_play"
                          ? () =>
                              openEndSession({
                                id: court.id,
                                label: court.label,
                              })
                          : undefined
                      }
                      onReassign={(group) =>
                        openReassign({
                          group,
                          sourceCourtId: court.id,
                          sourceCourtName: court.label,
                        })
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Dialog
        open={assignmentConfirmation !== null}
        onClose={() => {
          if (busyAction === null) setAssignmentConfirmation(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        {assignmentConfirmation ? (
          <>
            <DialogTitle>
              Assign {assignmentConfirmation.entryLabel}
            </DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ mb: 2 }}>
                Confirm the court and server-timed play window.
              </DialogContentText>
              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "7rem minmax(0, 1fr)",
                  },
                  gap: 1.5,
                }}
              >
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  Court
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  {assignmentConfirmation.court.label}
                </Typography>
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  Play window
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {formatFacilityTime(
                    assignmentConfirmation.startsAt,
                    assignmentConfirmation.timezone,
                  )}
                  {" - "}
                  {formatFacilityTime(
                    assignmentConfirmation.endsAt,
                    assignmentConfirmation.timezone,
                    true,
                  )}
                </Typography>
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  Notifications
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {assignmentConfirmation.entry.groupSize ?? 1}{" "}
                  {(assignmentConfirmation.entry.groupSize ?? 1) === 1
                    ? "player will be notified"
                    : "players will be notified"}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAssignmentConfirmation(null)}
                disabled={busyAction !== null}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() =>
                  callToSelectedCourt(
                    assignmentConfirmation.entry,
                    assignmentConfirmation.court.id,
                    assignmentConfirmation.assignmentDurationMinutes,
                  )
                }
                disabled={busyAction !== null || !actionsCurrent}
              >
                {busyAction !== null ? "Assigning…" : "Assign court"}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {/* Add walk-in dialog */}
      <Dialog
        open={walkInOpen}
        onClose={() => {
          if (walkInSaving) return;
          setWalkInCustomer(null);
          setWalkInError(null);
          setWalkInOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { maxHeight: "calc(100dvh - 2rem)" } } }}
      >
        <DialogTitle>Add walk-in party</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Add a checked-in customer to the back of the drop-in queue.
            </DialogContentText>
            {walkInError ? (
              <Alert severity="error" aria-live="assertive">
                {walkInError}
              </Alert>
            ) : null}
            <CustomerPicker
              facilityId={facilityId}
              facilityName={facility?.name ?? "Selected facility"}
              activeSessionsOnly
              searchActiveCustomers={searchActiveWalkInCustomers}
              value={walkInCustomer}
              disabled={walkInSaving}
              onChange={(customer) => {
                setWalkInCustomer(customer);
                setWalkInError(null);
              }}
            />
            <FormControl fullWidth>
              <InputLabel id="walk-in-format-label">Format</InputLabel>
              <Select
                labelId="walk-in-format-label"
                id="walk-in-format"
                label="Format"
                value={walkInMode}
                onChange={(event) =>
                  setWalkInMode(
                    event.target.value as StaffWalkInPartyRequest["mode"],
                  )
                }
                disabled={walkInSaving}
              >
                <MenuItem value="normal">Any</MenuItem>
                <MenuItem value="ranked_singles">Singles</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setWalkInCustomer(null);
              setWalkInError(null);
              setWalkInOpen(false);
            }}
            disabled={walkInSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={addWalkIn}
            disabled={walkInSaving || walkInCustomer === null}
          >
            {walkInSaving ? "Adding…" : "Add to queue"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close / Reopen / Clear confirm dialog */}
      <Dialog
        open={confirmAction !== null}
        onClose={() => !confirmBusy && setConfirmAction(null)}
        fullWidth
        maxWidth="sm"
      >
        {confirmAction ? (
          <>
            <DialogTitle>{CONFIRM_COPY[confirmAction].title}</DialogTitle>
            <DialogContent dividers>
              <DialogContentText>
                {CONFIRM_COPY[confirmAction].description}
              </DialogContentText>
              {error || boardError ? (
                <Alert severity="error" aria-live="assertive" sx={{ mt: 2 }}>
                  {error ?? boardError}
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setConfirmAction(null)}
                disabled={confirmBusy}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color={
                  CONFIRM_COPY[confirmAction].destructive ? "error" : "primary"
                }
                onClick={runConfirmAction}
                disabled={
                  confirmBusy ||
                  !actionsCurrent ||
                  !confirmActionMatchesSnapshot
                }
              >
                {confirmBusy
                  ? CONFIRM_COPY[confirmAction].busyLabel
                  : CONFIRM_COPY[confirmAction].confirmLabel}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={reassignAction !== null}
        onClose={() => !reassignBusy && setReassignAction(null)}
        fullWidth
        maxWidth="sm"
      >
        {reassignAction ? (
          <>
            <DialogTitle>
              Move {reassignAction.group.label} from{" "}
              {reassignAction.sourceCourtName}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <DialogContentText>
                  The current timer continues. Choose a court that remains open
                  through the assignment end time.
                </DialogContentText>
                <Typography
                  component="label"
                  htmlFor="reassign-destination-court"
                  variant="body2"
                  sx={{ fontWeight: 600 }}
                >
                  Destination court
                </Typography>
                <ManualCourtPicker
                  entryLabel={reassignAction.group.label}
                  controlId="reassign-destination-court"
                  accessibleName="Destination court"
                  options={reassignCourtOptions()}
                  value={reassignCourtId}
                  disabled={
                    reassignBusy || !actionsCurrent || reassignPreview === null
                  }
                  onValueChange={(courtId) => {
                    setReassignCourtId(courtId);
                    setRecoveryDialogError(null);
                  }}
                />
                {reassignBusy && reassignPreview === null ? (
                  <Typography variant="caption" color="text.secondary">
                    Checking destination courts…
                  </Typography>
                ) : reassignPreview ? (
                  <Typography variant="caption" color="text.secondary">
                    Timer ends at{" "}
                    {formatFacilityTime(
                      reassignAction.group.expiresAt,
                      reassignPreview.timezone,
                      true,
                    )}
                    .
                  </Typography>
                ) : null}
                <RecoveryFields
                  action="reassign"
                  reasons={recoveryReasonsFor("reassign")}
                  reasonCode={recoveryReasonCode}
                  note={recoveryNote}
                  disabled={reassignBusy || !actionsCurrent}
                  onReasonChange={(value) => {
                    setRecoveryReasonCode(value);
                    setRecoveryDialogError(null);
                  }}
                  onNoteChange={setRecoveryNote}
                />
                {recoveryDialogError ? (
                  <Alert severity="error">{recoveryDialogError}</Alert>
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setReassignAction(null)}
                disabled={reassignBusy}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={runReassign}
                disabled={
                  reassignBusy ||
                  !actionsCurrent ||
                  !reassignCourtId ||
                  !recoveryReasonCode
                }
              >
                {reassignBusy ? "Moving…" : "Move party"}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={waitingRecovery !== null}
        onClose={() => {
          if (busyAction === null) setWaitingRecovery(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        {waitingRecovery ? (
          <>
            <DialogTitle>
              {waitingRecovery.kind === "skip"
                ? `Skip ${waitingRecovery.label}?`
                : `Remove ${waitingRecovery.label} from the queue?`}
            </DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ mb: 2 }}>
                {waitingRecovery.kind === "skip"
                  ? "The party stays checked in and moves behind the other parties in its current line."
                  : "The party leaves the active queue and this action is recorded in its history."}
              </DialogContentText>
              <RecoveryFields
                action={waitingRecovery.kind}
                reasons={recoveryReasonsFor(waitingRecovery.kind)}
                reasonCode={recoveryReasonCode}
                note={recoveryNote}
                disabled={busyAction !== null || !actionsCurrent}
                onReasonChange={(value) => {
                  setRecoveryReasonCode(value);
                  setRecoveryDialogError(null);
                }}
                onNoteChange={setRecoveryNote}
              />
              {recoveryDialogError ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {recoveryDialogError}
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setWaitingRecovery(null)}
                disabled={busyAction !== null}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color={waitingRecovery.kind === "remove" ? "error" : "primary"}
                onClick={runWaitingRecovery}
                disabled={
                  busyAction !== null || !actionsCurrent || !recoveryReasonCode
                }
              >
                {busyAction !== null
                  ? waitingRecovery.kind === "skip"
                    ? "Skipping…"
                    : "Removing…"
                  : waitingRecovery.kind === "skip"
                    ? "Skip party"
                    : "Remove party"}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {/* End session confirm dialog (courts panel) — irreversible: frees the court. */}
      <Dialog
        open={endSessionCourt !== null}
        onClose={() => !endSessionBusy && setEndSessionCourt(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          End the session on {endSessionCourt?.label ?? "this court"}?
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            This frees the court immediately and the next waiting party is
            called on. This cannot be undone.
          </DialogContentText>
          <RecoveryFields
            action="end_session"
            reasons={recoveryReasonsFor("end_session")}
            reasonCode={recoveryReasonCode}
            note={recoveryNote}
            disabled={endSessionBusy || !actionsCurrent}
            onReasonChange={(value) => {
              setRecoveryReasonCode(value);
              setRecoveryDialogError(null);
            }}
            onNoteChange={setRecoveryNote}
          />
          {recoveryDialogError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {recoveryDialogError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setEndSessionCourt(null)}
            disabled={endSessionBusy}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={runEndSession}
            disabled={endSessionBusy || !actionsCurrent || !recoveryReasonCode}
          >
            {endSessionBusy ? "Ending…" : "End session"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function StatusMetric({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <Box sx={{ minWidth: 88 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "baseline" }}>
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      </Stack>
    </Box>
  );
}

function WaitingActionsMenu({
  actionLabel,
  disabled,
  disabledReason,
  canSkip,
  canRemove,
  onSkip,
  onRemove,
}: {
  actionLabel: string;
  disabled: boolean;
  disabledReason: string | null;
  canSkip: boolean;
  canRemove: boolean;
  onSkip: () => void;
  onRemove: () => void;
}) {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const closeMenu = () => setAnchorElement(null);

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Actions for ${actionLabel}`}
        aria-haspopup="menu"
        aria-expanded={anchorElement ? "true" : undefined}
        title={disabledReason ?? `Actions for ${actionLabel}`}
        disabled={disabled}
        onClick={(event) => setAnchorElement(event.currentTarget)}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        open={anchorElement !== null}
        onClose={closeMenu}
      >
        {canSkip ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              onSkip();
            }}
          >
            <SkipNextIcon fontSize="small" sx={{ mr: 1 }} />
            Skip
          </MenuItem>
        ) : null}
        {canRemove ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              onRemove();
            }}
          >
            <RemoveCircleIcon fontSize="small" sx={{ mr: 1 }} />
            Remove from queue
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

function courtLineGroupLabel(group: CourtBoardLineGroup): string {
  return group.player_count === 1 ? "Solo" : `Party of ${group.player_count}`;
}

function courtLineGroupMeta(group: CourtBoardLineGroup): string {
  const parts = [`Position ${group.position}`];
  if (group.estimated_wait_minutes != null) {
    parts.push(`${group.estimated_wait_minutes} min estimate`);
  }
  return parts.join(" · ");
}

function BoardName({ player }: { player: CourtBoardPresence }) {
  return isResolvedName(player.display_name, player.presence_id) ? (
    <Typography
      component="span"
      variant="body2"
      sx={{ overflowWrap: "anywhere" }}
    >
      {player.display_name}
    </Typography>
  ) : (
    <Typography component="span" variant="body2" color="text.secondary">
      &#x2014;
    </Typography>
  );
}

function CourtDetailsDialog({
  court,
  boardEntry,
  onCourtEntry,
  open,
  onOpenChange,
  onDismissAutoFocus,
  disabled,
  onReassign,
  onEndSession,
}: {
  court: Court;
  boardEntry: CourtBoardEntry;
  onCourtEntry: OnCourtEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDismissAutoFocus: () => void;
  disabled: boolean;
  onReassign: (group: ReassignableOnCourtPlayingGroup) => void;
  onEndSession?: () => void;
}) {
  const players = boardEntry.players ?? [];
  const lineGroups = boardEntry.line_groups ?? [];
  const omittedGroups = Math.max(
    0,
    boardEntry.line_group_count - lineGroups.length,
  );
  const lineSummary = formatCourtLineSummary(
    boardEntry.line_group_count,
    boardEntry.line_player_count,
  );
  const playingGroups = onCourtEntry?.playingGroups ?? [];
  const dismissDetails = () => {
    onOpenChange(false);
    window.setTimeout(onDismissAutoFocus, 0);
  };
  const transitionFromDetails = (openNextDialog: () => void) => {
    onOpenChange(false);
    openNextDialog();
  };

  return (
    <Dialog
      open={open}
      onClose={dismissDetails}
      fullWidth
      maxWidth="sm"
      disableRestoreFocus
    >
      <DialogTitle sx={{ pr: 7 }}>
        {court.label} details
        <IconButton
          aria-label="Close"
          size="small"
          onClick={dismissDetails}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "75vh" }}>
        <DialogContentText sx={{ mb: 2 }}>
          {getCourtAssignmentLabel(boardEntry)}
          {lineSummary ? ` · ${lineSummary}` : ""}
        </DialogContentText>

        <Stack spacing={3}>
          <Box component="section" aria-labelledby={`playing-${court.id}`}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                component="h3"
                variant="subtitle2"
                id={`playing-${court.id}`}
              >
                Playing now
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {players.length}/{boardEntry.capacity}
              </Typography>
            </Stack>
            {playingGroups.length > 0 ? (
              <Stack
                component="ul"
                spacing={1}
                sx={{ m: 0, p: 0, listStyle: "none" }}
              >
                {playingGroups.map((group) => (
                  <Paper
                    component="li"
                    key={group.playingGroupKey}
                    variant="outlined"
                    sx={{ p: 1.25 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          useFlexGap
                          sx={{
                            alignItems: "baseline",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {group.groupSize === 1
                              ? "Solo"
                              : `Party of ${group.groupSize}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ref {group.groupReference}
                          </Typography>
                        </Stack>
                        <Stack
                          component="ul"
                          direction="row"
                          useFlexGap
                          sx={{
                            m: 0,
                            p: 0,
                            listStyle: "none",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {group.members.map((member) => (
                            <Box component="li" key={member.presence_id}>
                              <BoardName player={member} />
                            </Box>
                          ))}
                        </Stack>
                        {group.assignmentReason !== null ||
                        group.remainingMinutes !== null ? (
                          <Stack
                            direction="row"
                            useFlexGap
                            sx={{ mt: 0.5, flexWrap: "wrap", gap: 1 }}
                          >
                            {group.assignmentReason !== null ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {group.assignmentReason}
                              </Typography>
                            ) : null}
                            {group.remainingMinutes !== null ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {group.remainingMinutes} min remaining
                              </Typography>
                            ) : null}
                          </Stack>
                        ) : null}
                      </Box>
                      {isReassignablePlayingGroup(group) ? (
                        <IconButton
                          size="small"
                          aria-label={`Reassign ${group.label} from ${court.label}`}
                          title="Reassign court"
                          onClick={() => {
                            transitionFromDetails(() => onReassign(group));
                          }}
                          disabled={disabled}
                        >
                          <SwapHorizIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : players.length > 0 ? (
              <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
                {players.map((player) => (
                  <Box component="li" key={player.presence_id}>
                    <BoardName player={player} />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No game in progress.
              </Typography>
            )}
          </Box>

          <Box
            component="section"
            aria-labelledby={`waiting-${court.id}`}
            sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                component="h3"
                variant="subtitle2"
                id={`waiting-${court.id}`}
              >
                Waiting
              </Typography>
              {boardEntry.line_group_count > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {boardEntry.line_group_count}{" "}
                  {boardEntry.line_group_count === 1 ? "party" : "parties"}
                </Typography>
              ) : null}
            </Stack>
            {lineGroups.length > 0 ? (
              <Stack component="ol" spacing={1.5} sx={{ m: 0, pl: 2.5 }}>
                {lineGroups.map((group) => {
                  const isNextUp =
                    boardEntry.next_up_group?.line_group_key ===
                    group.line_group_key;
                  return (
                    <Box
                      component="li"
                      key={group.line_group_key}
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        pb: 1.5,
                        "&:last-child": { borderBottom: 0, pb: 0 },
                      }}
                    >
                      <Stack
                        direction="row"
                        useFlexGap
                        sx={{
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {courtLineGroupLabel(group)}
                        </Typography>
                        <Stack
                          direction="row"
                          useFlexGap
                          sx={{ flexWrap: "wrap", gap: 0.5 }}
                        >
                          {isNextUp ? (
                            <Chip
                              size="small"
                              color="primary"
                              label="Next up"
                            />
                          ) : null}
                          {group.is_current_group ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label="Your group"
                            />
                          ) : null}
                        </Stack>
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {courtLineGroupMeta(group)}
                      </Typography>
                      <Typography
                        component="time"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                        dateTime={group.joined_at}
                      >
                        Joined{" "}
                        {new Intl.DateTimeFormat(undefined, {
                          hour: "numeric",
                          minute: "2-digit",
                        }).format(new Date(group.joined_at))}
                      </Typography>
                      <Stack
                        component="ul"
                        spacing={0.5}
                        sx={{ mt: 0.5, pl: 2.5 }}
                      >
                        {group.members.map((member) => (
                          <Box component="li" key={member.presence_id}>
                            <BoardName player={member} />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No parties waiting.
              </Typography>
            )}
            {omittedGroups > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                +{omittedGroups} more{" "}
                {omittedGroups === 1 ? "party" : "parties"}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </DialogContent>
      {onEndSession ? (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<StopCircleIcon />}
            aria-label={`End session on ${court.label}`}
            disabled={disabled}
            onClick={() => {
              transitionFromDetails(onEndSession);
            }}
          >
            End session
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

function CourtTile({
  court,
  boardEntry,
  onCourtEntry,
  disabled,
  canManageCourts,
  onToggleMaintenance,
  onEndSession,
  onReassign,
}: {
  court: Court;
  boardEntry: CourtBoardEntry | null;
  onCourtEntry: OnCourtEntry | null;
  disabled: boolean;
  canManageCourts: boolean;
  onToggleMaintenance: () => void;
  onEndSession?: () => void;
  onReassign: (group: ReassignableOnCourtPlayingGroup) => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (boardEntry === null) setDetailsOpen(false);
  }, [boardEntry]);
  const inGame = boardEntry?.occupancy_state === "in_play";
  const showSecondaryOccupancy =
    inGame && boardEntry?.operational_state !== "operational";
  const isAvailable = boardEntry ? isCourtReadyToAssign(boardEntry) : false;
  const isUnassignableEmptyCourt =
    boardEntry?.operational_state === "operational" &&
    boardEntry.occupancy_state === "available" &&
    !isAvailable;
  const displayLabel = boardEntry
    ? getCourtAssignmentLabel(boardEntry)
    : "Unavailable";
  const lineSummary = boardEntry
    ? formatCourtLineSummary(
        boardEntry.line_group_count,
        boardEntry.line_player_count,
      )
    : null;
  const announcement = isUnassignableEmptyCourt
    ? [
        court.label,
        "Operational",
        "Unavailable for assignment",
        `${boardEntry.players?.length ?? 0} of ${boardEntry.capacity} players`,
        lineSummary,
      ]
        .filter(Boolean)
        .join(", ")
    : boardEntry
      ? formatCourtStatusAnnouncement({
          name: court.label,
          operationalState: boardEntry.operational_state,
          occupancyState: boardEntry.occupancy_state,
          playerCount: boardEntry.players?.length ?? 0,
          capacity: boardEntry.capacity,
          availableSlots: boardEntry.available_slots,
          isPrivate: boardEntry.is_private,
          lineGroupCount: boardEntry.line_group_count,
          linePlayerCount: boardEntry.line_player_count,
        })
      : `${court.label}, Unavailable`;
  return (
    <>
      <Paper
        role="group"
        aria-label={announcement}
        variant="outlined"
        sx={{
          minWidth: 0,
          minHeight: 148,
          p: 1.25,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          borderColor: isAvailable ? "success.main" : "warning.main",
          bgcolor: (theme) =>
            alpha(
              isAvailable
                ? theme.palette.success.main
                : theme.palette.warning.main,
              0.06,
            ),
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ minHeight: 40, minWidth: 0, overflowWrap: "anywhere" }}
        >
          {court.label}
        </Typography>

        {boardEntry != null ? (
          <Stack
            data-testid={`court-board-state-${court.id}`}
            spacing={0.25}
            sx={{ minHeight: 40 }}
          >
            {showSecondaryOccupancy ? (
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                In play
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {boardEntry.players?.length ?? 0}/{boardEntry.capacity}{" "}
              {inGame ? "playing" : "occupied"}
            </Typography>
            {lineSummary ? (
              <Typography variant="caption" color="text.secondary">
                {lineSummary}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        <Stack
          direction="row"
          useFlexGap
          sx={{
            mt: "auto",
            minWidth: 0,
            gap: 0.5,
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Chip
            size="small"
            label={displayLabel}
            color={isAvailable ? "success" : "default"}
            variant={isAvailable ? "filled" : "outlined"}
            sx={{ maxWidth: "100%" }}
          />
          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            {boardEntry != null ? (
              <IconButton
                ref={detailsButtonRef}
                size="small"
                aria-label={`View ${court.label} details`}
                title="View court details"
                onClick={() => setDetailsOpen(true)}
              >
                <GroupsIcon fontSize="small" />
              </IconButton>
            ) : null}
            {canManageCourts &&
            boardEntry != null &&
            boardEntry.operational_state !== "closed" ? (
              <IconButton
                size="small"
                aria-label={
                  boardEntry.operational_state === "maintenance"
                    ? `Return ${court.label} to service`
                    : `Put ${court.label} in maintenance`
                }
                title={
                  boardEntry.operational_state === "maintenance"
                    ? "Return to service"
                    : "Put in maintenance"
                }
                onClick={onToggleMaintenance}
                disabled={disabled}
              >
                {boardEntry.operational_state === "maintenance" ? (
                  <LockOpenIcon fontSize="small" />
                ) : (
                  <BuildIcon fontSize="small" />
                )}
              </IconButton>
            ) : null}
            {inGame && onEndSession ? (
              <IconButton
                size="small"
                color="error"
                aria-label={`End session on ${court.label}`}
                title="End session"
                onClick={onEndSession}
                disabled={disabled}
              >
                <StopCircleIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
      {boardEntry ? (
        <CourtDetailsDialog
          court={court}
          boardEntry={boardEntry}
          onCourtEntry={onCourtEntry}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          onDismissAutoFocus={() => detailsButtonRef.current?.focus()}
          disabled={disabled}
          onReassign={onReassign}
          onEndSession={onEndSession}
        />
      ) : null}
    </>
  );
}
